import { useCrypt } from '@composables/useCrypt';
import { type AppData } from '@global-types/appData';
import { Logger } from '@tools/logger/logger';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

/**
 * No need to use storeToRefs() on this store because based on data provided by twig
 */
export const useAppDataStore = defineStore('appData', () => {
  const appData = ref<AppData>();

  const init = () => {
    if (appData.value) {
      return;
    }

    const el: HTMLElement | null = document.getElementById('app-data');
    if (!el) {
      Logger.error('App data not found in template');
      return;
    }

    appData.value = JSON.parse(el.textContent) as AppData;
  };

  const user = computed<AppData['user']>(() => appData.value?.user ?? null);
  const accountManager = computed<AppData['account_manager']>(() => appData.value?.account_manager ?? null);
  const isAccountManagerOnline = computed<AppData['is_account_manager_online']>(() => appData.value?.is_account_manager_online ?? false);
  const organizationUsers = computed<AppData['organization_users']>(() => appData.value?.organization_users ?? []);
  const isImpersonated = computed<AppData['is_impersonated']>(() => appData.value?.is_impersonated ?? false);
  const isMaintenance = computed<AppData['is_maintenance']>(() => appData.value?.is_maintenance ?? false);
  const userNotifications = computed<AppData['user_notifications']>(() => appData.value?.user_notifications ?? {
    unread_total: 0,
    unread_messages: 0,
    unread_match_proposal: 0,
  } as AppData['user_notifications']);

  const impersonatedPipedriveUser = computed<AppData['impersonated_pipedrive_user']>(() => appData.value?.impersonated_pipedrive_user ?? null);
  const navbarData = computed<AppData['navbar'] | null>(() => appData.value?.navbar ?? null);
  const baseUrl = computed<AppData['base_url'] | null>(() => appData.value?.base_url ?? null);

  const { decryptBase64 } = useCrypt();
  const googleApiKey = computed<AppData['google_api_key'] | undefined>(() => decryptBase64(appData.value?.google_api_key));
  const targomoApiKey = computed<AppData['targomo_api_key'] | undefined>(() => decryptBase64(appData.value?.targomo_api_key));
  const recaptchaKey = computed<AppData['recaptcha_key'] | null>(() => appData.value?.recaptcha_key ?? null);
  const sessionToken = crypto.randomUUID();

  return {
    init,
    user,
    organizationUsers,
    isImpersonated,
    isMaintenance,
    userNotifications,
    impersonatedPipedriveUser,
    navbarData,
    accountManager,
    isAccountManagerOnline,
    baseUrl,
    googleApiKey,
    targomoApiKey,
    recaptchaKey,
    sessionToken,
  };
});
